import { useTranslation } from "react-i18next";

import { ArtifactGrid } from "../ArtifactGrid";

import "./DocumentElementCard.css";
import { useDispatch, useSelector } from "react-redux";
import { AssetType } from "../../types/AssetType";
import { ElementImportance } from "../../types/ElementImportance";
import { LoggedInUserType } from "../../types/LoggedInUserType";
import getUserTypeText from "../../helpers/users";
import ElementDocument from "../../types/ElementDocument";
import {assetUploadedBy} from "../../helpers/uploaded_by";
import { DateSelector } from "../DateSelector/DateSelector";
import { updateDocumentExpiryDate } from "../../store/actions/Inspections";
import { convertEpochDate } from "../../helpers/dates";
import { Spinner } from '@shopify/polaris';
import {useEffect, useRef, useState} from "react";

interface DocProps {
    elementId: string;
}

export default function DocumentElementCard({ elementId }: DocProps) {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    
    const currentElement = useSelector((state: any) =>
        state.portal.activeInspection.sections.find(
            (section: any) => section.Id === elementId
        )
    );

    const currentDocuments: ElementDocument[] = useSelector(
        (state: any) =>
            state.portal.activeInspection.sections.find(
                (section: any) => section.Id === elementId
            ).Documents
    );

    const surveyorCompletionStatus = useSelector((state: any) => {return state.portal.activeInspection.SurveyorCompletionStatus});
    const inspectionTypeAllowedReuse = useSelector((state: any) => {return state.portal.activeInspection.inspectionTypeAllowedReuse});

    const userType: LoggedInUserType = useSelector((state: any) => state.portal.userType);
    const assetUploader = getUserTypeText(userType);

    const [initialDate, setInitialDate] = useState(currentElement.expiryDate ? convertEpochDate(currentElement.expiryDate) : undefined);
    const globalExpiryDate : string = useSelector((state: any) => state.portal.globalExpiryDate);

    useEffect(() => {
         if (!isNaN(new Date(globalExpiryDate).getTime())) {
             setInitialDate(globalExpiryDate)
             dispatch(
                 updateDocumentExpiryDate(
                     globalExpiryDate,
                     currentElement.Id,
                     currentElement.Documents
                 )
             )
         }
    }, [globalExpiryDate]);



    let elementImportance;

    switch (currentElement.ElementImportance) {
        // As per product requirements, set the portal UI to display REQUIRED if the element importance is set to PREFERRED
        case ElementImportance.Preferred:
        case ElementImportance.Required:
            elementImportance = (
                <div className="document-card-header-required">
                    <p>{t("inspection.documents.required")}</p>
                </div>
            );
            break;
        case ElementImportance.Optional:
            elementImportance = (
                <div className="document-card-header-optional">
                    <p>{t("inspection.documents.optional")}</p>
                </div>
            );
            break;
    }

    return (
        <div
            className="document-card"
            data-cy={currentElement.Name.replace(/\s/g, "")}
        >
            {currentDocuments.length >= currentElement.MinItems &&
            currentElement.MinItems != null ? (
                <div className="document-card-header-valid">
                    <p>{t("inspection.documents.valid")}</p>
                </div>
            ) : (
                elementImportance
            )}
            <div className="document-card-title">{currentElement.Name}</div>
            <div className="document-card-body">
                <ArtifactGrid
                    assetType={AssetType.Document}
                    elementId={currentElement.Id}
                    assetSize="small"
                />
            </div>

            <div>

                {currentElement.MinItems !== null && currentDocuments.length >= currentElement.MinItems ? (
                    <div className={"added-by"}>
                        <p>
                            {t("inspection.documents.added_by")}{" "}
                            {assetUploadedBy(currentDocuments, t) ?? assetUploader}
                        </p>
                    </div>
                ) : (
                    <></>
                )}

            {/* Card Footer */}
            <div className={"added-by"}>
                {
                    currentElement.savingExpiryDate && (
                        <Spinner size="small" />
                    )
                }
                {
                    inspectionTypeAllowedReuse &&
                    currentElement.ReUseDocuments && 
                    !currentElement.savingExpiryDate &&
                        (
                            <DateSelector
                                dateValue={initialDate}
                                dateError={currentElement.expiryDateError === true ? t("reuseDocuments.invalidDate") : null}
                                disabled={surveyorCompletionStatus.DocumentsComplete}
                                onChange={
                                    (t) => {
                                        setInitialDate(t.target.value);

                                        dispatch(
                                            updateDocumentExpiryDate(
                                                t.target.value, 
                                                currentElement.Id,
                                                currentElement.Documents
                                            )
                                        )
                                    }}
                            />
                        )
                }
            </div>
                {currentElement.PreliminaryReportDescription &&
                currentElement.PreliminaryReportDescription.length > 0 ? (
                    <section className={"hint-text"}>
                        {currentElement.PreliminaryReportDescription}
                    </section>
                ) : null}
            </div>

        </div>
    );
}
